import {
  getVoiceColor,
  mixVoiceColor,
  VOICE_LISTENING_SPAN_MS,
  VOICE_SPEAKING_SPAN_MS,
  voiceGradientColor,
  type VoiceAvatarMode,
  type VoiceColor,
} from './voice-companion-colors';

const WHITE = '#fff';
const INK = '#17171a';
/** Relative luminance of the two eye inks. */
const WHITE_LUMINANCE = 1;
const INK_LUMINANCE = 0.0088;

const SAMPLE_MS = 25;
/** How long a mode or look change blends the old body colour into the new. */
export const VOICE_TRANSITION_MS = 300;
/** WCAG 1.4.11 contrast for meaningful graphics. */
export const VOICE_EYE_MIN_CONTRAST = 3;

const eyeColorByMode = new Map<string, string>();

/** OKLCH to WCAG relative luminance, clamping out-of-gamut channels. */
export function voiceColorLuminance([lightness, chroma, hue]: VoiceColor) {
  const angle = (hue * Math.PI) / 180;
  const a = chroma * Math.cos(angle);
  const b = chroma * Math.sin(angle);
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const channel = (value: number) => Math.max(0, Math.min(1, value));
  const red = channel(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s);
  const green = channel(
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
  );
  const blue = channel(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrast(first: number, second: number) {
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/** Every body colour a mode can paint, gradient extremes included. */
export function voiceModeShades(
  mode: VoiceAvatarMode,
  base: VoiceColor
): VoiceColor[] {
  const span =
    mode === 'idle'
      ? 0
      : mode === 'listening'
        ? VOICE_LISTENING_SPAN_MS
        : VOICE_SPEAKING_SPAN_MS;
  const shades: VoiceColor[] = [];
  for (let time = 0; time <= span; time += SAMPLE_MS) {
    const color = getVoiceColor(mode, time, base);
    shades.push(
      voiceGradientColor(color, -1),
      color,
      voiceGradientColor(color, 1)
    );
  }
  return shades;
}

/** The lowest eye contrast an ink reaches over a mode's shades. */
export function voiceEyeContrast(
  eye: string,
  mode: VoiceAvatarMode,
  base: VoiceColor
): number {
  const eyeLuminance = eye === WHITE ? WHITE_LUMINANCE : INK_LUMINANCE;
  return Math.min(
    ...voiceModeShades(mode, base).map((shade) =>
      contrast(eyeLuminance, voiceColorLuminance(shade))
    )
  );
}

/**
 * The eye ink for a persona in one call mode: white like the sidebar blob
 * while it keeps 3:1 against every shade the mode paints, otherwise
 * whichever ink holds more contrast. One choice per mode, so the eyes never
 * flip during a listening or speaking pulse.
 */
export function voiceEyeColor(mode: VoiceAvatarMode, base: VoiceColor): string {
  const key = `${mode}|${base.join(',')}`;
  const cached = eyeColorByMode.get(key);
  if (cached) return cached;
  const white = voiceEyeContrast(WHITE, mode, base);
  const eye =
    white >= VOICE_EYE_MIN_CONTRAST ||
    white >= voiceEyeContrast(INK, mode, base)
      ? WHITE
      : INK;
  eyeColorByMode.set(key, eye);
  return eye;
}

/**
 * Where a mode or look change starts its colour blend: the last painted
 * colour, but only when the eyes keep their ink. When the ink changes, the
 * blend would put the new eyes on an old colour nobody checked, so the new
 * mode's own (contrast-checked) colours start at once instead.
 */
export function voiceTransitionStart(
  previousColor: VoiceColor | null,
  previousEye: string | null,
  eye: string,
  sameColorSeed: boolean
): VoiceColor | null {
  // A new persona colour repaints at once too: blending two hues passes
  // through colours (green, between violet and red) nobody checked.
  return previousColor && sameColorSeed && previousEye === eye
    ? previousColor
    : null;
}

/** The body colour at `timeMs` into a mode, blended from `from` at first. */
export function voiceTransitionColor(
  from: VoiceColor | null,
  mode: VoiceAvatarMode,
  timeMs: number,
  base: VoiceColor
): VoiceColor {
  const color = getVoiceColor(mode, timeMs, base);
  if (!from || timeMs >= VOICE_TRANSITION_MS) return color;
  return mixVoiceColor(
    from,
    color,
    1 - (1 - timeMs / VOICE_TRANSITION_MS) ** 4
  );
}
